import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { sharedReducer } from './reducer';
import { initialState as defaultState, SharedState } from './state';
import { SharedStateActions } from './actions';

type AsyncAction = (
  dispatch: Dispatch<SharedStateActions>,
  getState: () => SharedState,
) => Promise<void>;

export type DispatchWithThunk = (
  value: SharedStateActions | AsyncAction,
) => Promise<void>;

export type SharedAppContext = {
  state: SharedState;
  dispatch: DispatchWithThunk;
};

const SharedContext = createContext<SharedAppContext>({
  state: defaultState,
  dispatch: () => Promise.resolve(),
});

type SharedContextProviderProps = {
  children: React.ReactNode;
  initialState?: Partial<SharedState>;
};

const SharedContextProvider = ({
  initialState,
  children,
}: SharedContextProviderProps) => {
  const [state, dispatch] = useReducer(sharedReducer, {
    ...defaultState,
    ...initialState,
  });
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getState = useCallback(() => stateRef.current, []);

  const enhancedDispatch: DispatchWithThunk = useCallback(
    async (action: SharedStateActions | AsyncAction) => {
      if (typeof action === 'function') {
        return (action as AsyncAction)(dispatch, getState);
      } else {
        return dispatch(action as SharedStateActions);
      }
    },
    [dispatch, getState],
  );

  const context = {
    state,
    dispatch: enhancedDispatch,
  };

  return (
    <SharedContext.Provider value={context}>{children}</SharedContext.Provider>
  );
};

const useSharedAppContext = () => useContext(SharedContext);
const useSharedState = () => useSharedAppContext().state;
const useSharedDispatch = () => useSharedAppContext().dispatch;

const useJobsState = () => useSharedState().jobs;

export {
  SharedContextProvider,
  useSharedAppContext,
  useSharedState,
  useSharedDispatch,
  useJobsState,
};
